// Dropdown — anchored popover panel for menus. Compose-first: the consumer
// provides the trigger and the panel content (rows use the .app-dropdown__item
// classes); this component owns the wrapper, positioning, and dismissal
// (Escape + outside click). Alignment uses logical insets so it flips in RTL.
//
// `portal` renders the panel to document.body with fixed positioning anchored
// to the trigger — use it when the dropdown sits inside a clipping container
// (e.g. a table with `overflow: auto`) that would otherwise crop the panel.
//
// `panelRole` opts out of the default `role='menu'` — pass 'none' when the
// consumer supplies its own semantic container inside the panel.

import {
  HTMLAttributes,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export interface DropdownProps extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose: () => void;
  /** The element that toggles the panel; rendered inside the anchored wrapper. */
  trigger: ReactNode;
  /** Panel edge to align with the trigger. Default 'end' (flips in RTL). */
  align?: 'start' | 'end';
  /** Panel width in px. Defaults to content width with a 200px minimum. */
  width?: number;
  /** Render the panel in a body portal (fixed positioning) to escape a
   *  clipping/overflow ancestor. Default false (absolute, in-flow). */
  portal?: boolean;
  /** Portal only — when the panel would run past the viewport bottom (fixed
   *  positioning can't be scrolled into view), place it above the trigger
   *  instead. Opt-in so existing portal menus keep their placement. */
  flip?: boolean;
  /** ARIA role for the panel. Default 'menu'. Pass 'none' when the consumer
   *  renders its own semantic container inside (e.g. a listbox), so the panel
   *  chrome doesn't inject a spurious menu into the accessibility tree. */
  panelRole?: 'menu' | 'listbox' | 'none';
  children: ReactNode;
}

const GAP = 8; // ≈ var(--space-2)
const PORTAL_WIDTH_FALLBACK = 200; // matches the panel's min-width

const Dropdown = ({
  open,
  onClose,
  trigger,
  align = 'end',
  width,
  portal = false,
  flip = false,
  panelRole = 'menu',
  children,
  className,
  ...rest
}: DropdownProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Ignore clicks inside the trigger wrapper OR the (possibly portaled) panel.
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
    };
  }, [open, onClose]);

  // Portal path: measure the trigger and pin the panel with fixed coords,
  // re-measuring on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || !portal) return undefined;
    const place = () => {
      const anchor = wrapperRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const rtl = getComputedStyle(anchor).direction === 'rtl';
      const w = width ?? PORTAL_WIDTH_FALLBACK;
      // 'end' aligns the panel's inline-end edge with the trigger's; 'start'
      // the inline-start edge. Inline edges flip under RTL.
      const alignEndOnRight = align === 'end' ? !rtl : rtl;
      let left = alignEndOnRight ? rect.right - w : rect.left;
      left = Math.min(Math.max(left, GAP), window.innerWidth - w - GAP);
      let top = rect.bottom + GAP;
      // The panel is already in the DOM (hidden at -9999) when this layout
      // effect runs, so its height is measurable. Flip above the trigger when
      // it overflows the viewport bottom and the space above fits it better.
      if (flip) {
        const h = panelRef.current?.offsetHeight ?? 0;
        const overflowsBelow = h > 0 && top + h > window.innerHeight - GAP;
        const roomAbove = rect.top - GAP * 2;
        if (overflowsBelow && roomAbove > window.innerHeight - rect.bottom - GAP * 2) {
          top = Math.max(rect.top - GAP - h, GAP);
        }
      }
      setCoords({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, portal, align, width, flip]);

  const panel = open ? (
    portal ? (
      <div
        ref={panelRef}
        className='app-dropdown__panel'
        role={panelRole}
        style={{
          position: 'fixed',
          top: coords?.top ?? -9999,
          left: coords?.left ?? -9999,
          width: width ?? PORTAL_WIDTH_FALLBACK,
          // Above the drawer/modal overlay layer (1000) so a portaled dropdown
          // launched from inside one floats over it; below toast (~9999).
          zIndex: 1100,
        }}
      >
        {children}
      </div>
    ) : (
      <div
        ref={panelRef}
        className={`app-dropdown__panel app-dropdown__panel--${align}`}
        role={panelRole}
        style={width ? { width } : undefined}
      >
        {children}
      </div>
    )
  ) : null;

  return (
    <div ref={wrapperRef} className={`app-dropdown${className ? ` ${className}` : ''}`} {...rest}>
      {trigger}
      {portal ? panel && createPortal(panel, document.body) : panel}
    </div>
  );
};

export default Dropdown;
